import { ComponentStyleSource, getComponentStyleSource, getEditableTextComponentStyleSource } from '@wix/a11y-adapters';
import { blendColors, RuleId } from '@wix/a11y-rules';
import { useExperiments } from '@wix/fe-essentials/react';
import { ElementStatus, RuleListStatus } from '@wix/rules-panel-ui';
import { isNotOptional } from '@wix/seo-common-core';
import Color from 'color';
import { produce } from 'immer';
import { useCallback, useState } from 'react';
import { BiCtaNames } from '../../../../bi/types';
import { usePanelStateAtom } from '../../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../../hooks/use-bi-logger';
import { useGenericElement } from '../../../../hooks/use-generic-element';
import { PanoramaErrorNames, PanoramaTransactions } from '../../../../panorama/constants';
import { usePanoramaFunctions } from '../../../../panorama/use-panorama-functions';
import { useGlobalSectionState } from '../../../../rule-mappers/shared/use-global-section-state';
import { useRuleBase } from '../../../../rule-mappers/shared/use-rule-base';
import { getCompRefType } from '../../../../utils/get-comp-ref-type';
import { isEditor3Host } from '../../../../utils/get-editor-host';
import { hasSharedBaseComponentId, updateElementsColor } from '../helpers/update-elements-color';
import { ContrastRatioLevel } from '../types';
export const useColorContrastFixer = _ref => {
  let {
    elements,
    pageId,
    numOfElements
  } = _ref;
  const biLogger = useBiLogger();
  const {
    startPanoramaTransaction,
    finishPanoramaTransaction,
    reportErrorPanorama
  } = usePanoramaFunctions();
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    getRuleState,
    panelState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const {
    updateGlobalSectionStateIfNeeded,
    getGlobalSectionModifiedComponentIds
  } = useGlobalSectionState();
  const {
    experiments
  } = useExperiments();
  const enableGlobalSectionForIncompleteElements = experiments.enabled('specs.a11y.enableGlobalSectionForIncompleteElements');
  const {
    getComponent,
    isGenericElement
  } = useGenericElement({
    getComponentStyleSource,
    pageId,
    unknownValue: ComponentStyleSource.UNKNOWN
  });
  const [showLoader, setShowLoader] = useState(false);
  const onChangeElementsColor = useCallback(_ref2 => {
    let {
      textElement,
      ancestorElement
    } = _ref2;
    updateElementsColor({
      a11yWizardAdapter,
      ancestorElement,
      editorHost,
      getComponent,
      textElement
    });
    if (textElement) {
      const relatedElements = elements.filter(element => hasSharedBaseComponentId(textElement.id, element.elementId));
      relatedElements.forEach(relatedElement => {
        updateElementsColor({
          a11yWizardAdapter,
          ancestorElement,
          editorHost,
          getComponent,
          textElement: {
            className: textElement.className ?? '',
            color: textElement.color,
            id: relatedElement.elementId
          }
        });
      });
    }
  }, [editorHost, getComponent, a11yWizardAdapter, elements]);
  const updateElementsWithCommonAncestorId = useCallback(_ref3 => {
    let {
      currentUiElementId,
      ancestorId,
      ancestorBgColor
    } = _ref3;
    const updatedElements = [];
    elements.forEach(element => {
      var _element$affectingAnc;
      if (element.uiElementId === currentUiElementId) {
        return;
      }
      const commonAncestorId = (_element$affectingAnc = element.affectingAncestors) == null ? void 0 : _element$affectingAnc.find(_ref4 => {
        let [id] = _ref4;
        return id === ancestorId;
      });
      if (commonAncestorId) {
        var _element$affectingAnc2;
        const updatedAffectingAncestors = ((_element$affectingAnc2 = element.affectingAncestors) == null ? void 0 : _element$affectingAnc2.map(_ref5 => {
          let [id, ancestor, className] = _ref5;
          return [id, {
            ...ancestor,
            bgColor: id === ancestorId ? ancestorBgColor : ancestor.bgColor
          }, className];
        })) ?? [];
        const blendedBgColor = blendColors(updatedAffectingAncestors.map(_ref6 => {
          let [id, {
            bgColor,
            bgAlpha
          }] = _ref6;
          return new Color(id === ancestorId ? ancestorBgColor : bgColor).alpha(isNotOptional(bgAlpha) ? bgAlpha : 1);
        }));
        const currentContrastRatio = new Color(element.textColor).contrast(blendedBgColor);
        const updatedElement = {
          ...element,
          affectingAncestors: updatedAffectingAncestors,
          isValid: currentContrastRatio >= element.minimalRatio
        };
        updatedElements.push(updatedElement);
      }
    });
    return updatedElements;
  }, [elements]);
  const updateElementsWithSharedTextColor = useCallback(_ref7 => {
    let {
      currentElement,
      newTextColor
    } = _ref7;
    const updatedElements = [];
    elements.forEach(element => {
      if (element.uiElementId === currentElement.uiElementId) {
        return;
      }
      if (hasSharedBaseComponentId(currentElement.elementId, element.elementId)) {
        const newTextColorObj = new Color(newTextColor);
        const elementBgColor = new Color(element.bgColor);
        const elementContrastRatio = newTextColorObj.contrast(elementBgColor);
        const updatedElement = {
          ...element,
          isValid: elementContrastRatio >= element.minimalRatio,
          ratio: elementContrastRatio,
          textColor: newTextColor
        };
        updatedElements.push(updatedElement);
      }
    });
    return updatedElements;
  }, [elements]);
  const onApplyClick = useCallback(async _ref8 => {
    let {
      currentContrastRatioLevel,
      currentContrastRatio,
      elementIndex,
      element,
      textColorState,
      bgColorState,
      isValid,
      isIncomplete
    } = _ref8;
    startPanoramaTransaction(PanoramaTransactions.colorContrastRuleOnApply, {
      currentContrastRatio,
      currentContrastRatioLevel
    });
    biLogger.taskElementCtaClick({
      ctaName: BiCtaNames.Apply,
      index: elementIndex,
      status: currentContrastRatioLevel !== ContrastRatioLevel.Fail ? ElementStatus.Passed : ElementStatus.Failed
    });
    biLogger.taskElementApplyClick({
      index: elementIndex,
      status: currentContrastRatioLevel !== ContrastRatioLevel.Fail ? ElementStatus.Passed : ElementStatus.Failed,
      value: `${currentContrastRatioLevel === ContrastRatioLevel.Fail ? 'fail' : 'pass'}-${currentContrastRatio.toFixed(1)}:1`
    });
    setShowLoader(true);
    try {
      var _getRuleState;
      const updatedElement = {
        ...element,
        isValid: isValid ?? true,
        ratio: currentContrastRatio,
        ...((textColorState == null ? void 0 : textColorState.hasChanged) && {
          textColor: textColorState.hex
        })
      };
      const currentChangedElements = ((_getRuleState = getRuleState(pageId, RuleId.COLOR_CONTRAST)) == null || (_getRuleState = _getRuleState.state) == null ? void 0 : _getRuleState.changedElements) ?? new Map();
      const sharedComponentElements = textColorState != null && textColorState.hasChanged ? updateElementsWithSharedTextColor({
        currentElement: element,
        newTextColor: textColorState.hex
      }) : [];
      const elementsToUpdate = [updatedElement, ...(bgColorState != null && bgColorState.hasChanged ? updateElementsWithCommonAncestorId({
        ancestorBgColor: bgColorState.hex,
        ancestorId: bgColorState.compId,
        currentUiElementId: element.uiElementId
      }) : []), ...sharedComponentElements];
      const updatedElements = produce(currentChangedElements, draft => {
        elementsToUpdate.forEach(el => {
          draft.set(el.uiElementId, el);
        });
      });
      const updatedGlobalSectionDataMap = isIncomplete && !enableGlobalSectionForIncompleteElements ? panelState.globalSectionComponentsDataMap : updateGlobalSectionStateIfNeeded(new Map([[updatedElement.elementId, {
        bgColor: updatedElement.bgColor,
        componentId: updatedElement.elementId,
        textColor: updatedElement.textColor,
        ...(isIncomplete ? {
          isSkippedColorContrastElement: isValid
        } : {})
      }], ...(bgColorState != null && bgColorState.hasChanged ? [[bgColorState.compId, {
        bgColor: bgColorState.hex,
        componentId: bgColorState.compId,
        textColor: updatedElement.textColor
      }]] : [])]));
      const updatedPassedElements = new Map([...updatedElements].filter(_ref9 => {
        let [, el] = _ref9;
        return el.isValid;
      }));
      const updatedStatus = numOfElements === updatedPassedElements.size ? RuleListStatus.passed : RuleListStatus.failed;
      updateRuleStateAndVerifyIfNeeded(pageId, RuleId.COLOR_CONTRAST, {
        changedElements: updatedElements,
        status: updatedStatus
      }, getGlobalSectionModifiedComponentIds(updatedGlobalSectionDataMap));
    } catch (error) {
      reportErrorPanorama(error, PanoramaErrorNames.analyzeSite);
      console.error(`Failed to update color contrast for component`, error);
      setShowLoader(false);
      return;
    }
    setShowLoader(false);
    setIsApplyTriggered(true);
    finishPanoramaTransaction(PanoramaTransactions.colorContrastRuleOnApply, {
      currentContrastRatio,
      currentContrastRatioLevel
    });
  }, [startPanoramaTransaction, biLogger, setIsApplyTriggered, finishPanoramaTransaction, getRuleState, pageId, updateElementsWithSharedTextColor, updateElementsWithCommonAncestorId, enableGlobalSectionForIncompleteElements, panelState.globalSectionComponentsDataMap, updateGlobalSectionStateIfNeeded, numOfElements, updateRuleStateAndVerifyIfNeeded, getGlobalSectionModifiedComponentIds, reportErrorPanorama]);
  const isColorContrastGenericComponentForeground = (elementId, className) => {
    var _component, _component2;
    let component;
    try {
      component = getComponent(elementId);
    } catch (e) {
      console.error(e);
    }
    if (isEditor3Host(editorHost) && a11yWizardAdapter.isBuilderComponent({
      id: elementId,
      type: getCompRefType(editorHost)
    })) {
      return !(a11yWizardAdapter.isBuilderRichTextComponent != null && a11yWizardAdapter.isBuilderRichTextComponent({
        id: elementId,
        type: getCompRefType(editorHost)
      })) && a11yWizardAdapter.isGenericForegroundColorContrastBuilderComponent({
        id: elementId,
        type: getCompRefType(editorHost)
      }, className ?? '');
    }
    return !((_component = component) != null && _component.type) || getEditableTextComponentStyleSource((_component2 = component) == null ? void 0 : _component2.type) === ComponentStyleSource.UNKNOWN;
  };
  const isColorContrastGenericComponentBackground = (elementId, className) => {
    return isGenericElement(elementId, className);
  };
  return {
    getComponent,
    isColorContrastGenericComponentBackground,
    isColorContrastGenericComponentForeground,
    onApplyClick,
    onChangeElementsColor,
    setShowLoader,
    showLoader
  };
};
//# sourceMappingURL=use-color-contrast-fixer.js.map